import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api, apiPost } from '../lib/api.js';
import { getRecentConsoleErrors } from '../lib/consoleErrorBuffer.js';

const TABS = [
  { key: 'bug', label: 'Bug' },
  { key: 'feature', label: 'Feature request' },
  { key: 'function', label: 'Function request' },
];

const SEVERITIES = ['low', 'medium', 'high', 'critical'];

export default function FeedbackModal({ onClose, onResetPosition }) {
  const [category, setCategory] = useState('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Escape key closes the modal (a11y: matches ConfirmModal pattern).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function uploadScreenshotIfAny() {
    if (!file) return null;
    const fd = new FormData();
    fd.append('screenshot', file);
    const r = await api.post('/feedback/screenshot-upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  }

  async function submit(e) {
    e?.preventDefault?.();
    if (submitting) return;
    if (!title.trim() || !description.trim()) {
      toast.error('Title and description are required');
      return;
    }
    setSubmitting(true);
    try {
      let screenshotKey = null;
      if (file) {
        try {
          const r = await uploadScreenshotIfAny();
          screenshotKey = r?.screenshotKey || null;
        } catch (err) {
          toast.error(`Screenshot upload failed: ${err.message || err}`);
          // continue submitting without screenshot rather than losing the report
        }
      }
      const body = {
        category,
        title: title.trim(),
        description: description.trim(),
        severity: category === 'bug' ? severity : undefined,
        screenshotKey,
        context: {
          pageUrl: window.location.pathname + window.location.search,
          userAgent: navigator.userAgent,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          consoleErrors: getRecentConsoleErrors(),
        },
      };
      await apiPost('/feedback', body);
      toast.success('Thanks — sent. Easton reviews these every morning.');
      onClose?.();
    } catch (err) {
      toast.error(err.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-100 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.6)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="w-full max-w-xl mx-4 rounded-xl border"
        style={{ background: '#0b0b1a', borderColor: '#252550' }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: '#252550' }}>
          <h3 className="text-lg font-semibold" style={{ color: '#E040FB' }}>Send feedback</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white" aria-label="Close">×</button>
        </div>
        <div className="px-5 pt-4">
          <div className="flex gap-2 mb-4">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setCategory(t.key)}
                className="px-3 py-1.5 rounded-md text-sm"
                style={category === t.key
                  ? { background: '#7C3AED', color: '#fff' }
                  : { background: '#13132a', color: '#aaa', border: '1px solid #252550' }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <form onSubmit={submit} className="px-5 pb-5 space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">Title</label>
            <input
              type="text"
              maxLength={120}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={category === 'bug' ? 'Brief summary of the bug' : category === 'feature' ? 'What feature would help?' : 'What function should we add?'}
              className="w-full px-3 py-2 rounded-md text-sm"
              style={{ background: '#13132a', color: '#fff', border: '1px solid #252550' }}
              required
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">Description</label>
            <textarea
              maxLength={4000}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={6}
              placeholder={category === 'bug' ? 'Steps to reproduce, what you expected, what happened.' : 'Describe the desired outcome and why it matters.'}
              className="w-full px-3 py-2 rounded-md text-sm"
              style={{ background: '#13132a', color: '#fff', border: '1px solid #252550' }}
              required
            />
            <div className="text-[11px] text-gray-500 mt-1">{description.length}/4000</div>
          </div>
          {category === 'bug' && (
            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">Severity</label>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                className="w-full px-3 py-2 rounded-md text-sm"
                style={{ background: '#13132a', color: '#fff', border: '1px solid #252550' }}
              >
                {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">Screenshot (optional)</label>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-sm text-gray-300"
            />
          </div>
          <div className="flex items-center justify-between gap-2 pt-2">
            {onResetPosition ? (
              <button
                type="button"
                onClick={() => onResetPosition()}
                className="text-xs underline text-gray-400 hover:text-white"
              >Reset position</button>
            ) : <span />}
            <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md text-sm"
              style={{ background: '#13132a', color: '#aaa', border: '1px solid #252550' }}
            >Cancel</button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-md text-sm font-semibold"
              style={{ background: '#7C3AED', color: '#fff', opacity: submitting ? 0.6 : 1 }}
            >{submitting ? 'Sending…' : 'Send'}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
